import type { Team } from '../../schemas/common';
import { COV, hasCov } from '../../schemas/game';
import { type PlayerOfTheGame, pickPlayerOfTheGame } from './pog';
import type { GameState, PlayerTally, ReplayStatus } from './replay';

/**
 * The box score of one game (stats.md section 3, "honesty rules"). Owner: games. The game summary
 * shows it straight from the local fold, and stats imports `boxScore` instead of keeping a second
 * copy, so the numbers never differ between the phone and the server.
 *
 * A number is `null` when the game did not track it (the coverage bit is off). A quick game has
 * points and free throws but no misses, so FGA and FTA are null and no percentage can be drawn.
 * Nobody is credited with 0 for something nobody counted. Team points that no player owns
 * (`score_adjust`) are returned as `adjust`, so a team's lines plus its adjust equal its score.
 */

export interface BoxLine {
  playerId: string;
  team: Team;
  starter: boolean;
  pts: number | null;
  fgm: number | null;
  fga: number | null;
  /** Threes made and attempted. */
  tpm: number | null;
  tpa: number | null;
  ftm: number | null;
  fta: number | null;
  reb: number | null;
  oreb: number | null;
  dreb: number | null;
  ast: number | null;
  stl: number | null;
  blk: number | null;
  tov: number | null;
  pf: number | null;
}

export type BoxTotals = Omit<BoxLine, 'playerId' | 'team' | 'starter'>;

export interface BoxTeam {
  team: Team;
  score: number;
  /** Points on the board that no player is credited with (score_adjust). */
  adjust: number;
  lines: BoxLine[];
  totals: BoxTotals;
}

/** Which families of stats this game tracked. The UI hides a column when its flag is false. */
export interface BoxTracked {
  points: boolean;
  misses: boolean;
  freeThrows: boolean;
  rebounds: boolean;
  assists: boolean;
  steals: boolean;
  blocks: boolean;
  turnovers: boolean;
  fouls: boolean;
  lineup: boolean;
}

export interface BoxScore {
  status: ReplayStatus;
  winner: Team | null;
  teams: Record<Team, BoxTeam>;
  tracked: BoxTracked;
  /** Player of the Game, or null when there is none to pick (see pickPlayerOfTheGame). */
  potg: PlayerOfTheGame | null;
}

export function trackedFrom(coverage: number): BoxTracked {
  return {
    points: hasCov(coverage, COV.MAKES),
    misses: hasCov(coverage, COV.MISSES),
    freeThrows: hasCov(coverage, COV.FT),
    rebounds: hasCov(coverage, COV.REB),
    assists: hasCov(coverage, COV.AST),
    steals: hasCov(coverage, COV.STL),
    blocks: hasCov(coverage, COV.BLK),
    turnovers: hasCov(coverage, COV.TOV),
    fouls: hasCov(coverage, COV.PF),
    lineup: hasCov(coverage, COV.LINEUP),
  };
}

const NUM_KEYS = [
  'pts',
  'fgm',
  'fga',
  'tpm',
  'tpa',
  'ftm',
  'fta',
  'reb',
  'oreb',
  'dreb',
  'ast',
  'stl',
  'blk',
  'tov',
  'pf',
] as const;

function lineOf(t: PlayerTally, starter: boolean, tr: BoxTracked): BoxLine {
  const pts = tr.points ? t.pts : null;
  return {
    playerId: t.playerId,
    team: t.team,
    starter,
    pts,
    fgm: tr.points ? t.fgm : null,
    fga: tr.misses ? t.fga : null,
    tpm: tr.points ? t.tpm : null,
    tpa: tr.misses ? t.tpa : null,
    ftm: tr.freeThrows ? t.ftm : null,
    fta: tr.freeThrows && tr.misses ? t.fta : null,
    reb: tr.rebounds ? t.oreb + t.dreb : null,
    oreb: tr.rebounds ? t.oreb : null,
    dreb: tr.rebounds ? t.dreb : null,
    ast: tr.assists ? t.ast : null,
    stl: tr.steals ? t.stl : null,
    blk: tr.blocks ? t.blk : null,
    tov: tr.turnovers ? t.tov : null,
    pf: tr.fouls ? t.pf : null,
  };
}

function totalsOf(lines: readonly BoxLine[]): BoxTotals {
  const out = {} as Record<(typeof NUM_KEYS)[number], number | null>;
  for (const k of NUM_KEYS) {
    let sum: number | null = null;
    for (const l of lines) {
      const v = l[k];
      if (v !== null) sum = (sum ?? 0) + v;
    }
    out[k] = sum;
  }
  return out;
}

/** Makes over attempts as a whole percent, or null when there is no honest denominator. */
export function pct(made: number | null, att: number | null): number | null {
  if (made === null || att === null || att <= 0) return null;
  return Math.round((made / att) * 100);
}

/**
 * Build the box score from a replayed game. `starters` is the roster flag from game_players
 * (the replay state only knows who is on court now). Lines are sorted starters first, then by
 * points, then by id, so two phones draw the same table.
 */
export function boxScore(
  setup: { coverage: number; players: readonly { playerId: string; team: Team; starter: boolean }[] },
  state: GameState,
): BoxScore {
  const tr = trackedFrom(setup.coverage);
  const starterOf = new Map(setup.players.map((p) => [p.playerId, p.starter]));
  const build = (team: Team): BoxTeam => {
    const lines = Object.values(state.players)
      .filter((t) => t.team === team)
      .map((t) => lineOf(t, starterOf.get(t.playerId) ?? false, tr))
      .sort(
        (a, b) =>
          Number(b.starter) - Number(a.starter) ||
          (b.pts ?? 0) - (a.pts ?? 0) ||
          (a.playerId < b.playerId ? -1 : a.playerId > b.playerId ? 1 : 0),
      );
    const owned = lines.reduce((s, l) => s + (l.pts ?? 0), 0);
    return {
      team,
      score: state.score[team],
      adjust: tr.points ? state.score[team] - owned : state.score[team],
      lines,
      totals: totalsOf(lines),
    };
  };
  return {
    status: state.status,
    winner: state.winner,
    teams: { A: build('A'), B: build('B') },
    tracked: tr,
    potg: pickPlayerOfTheGame(state),
  };
}
